import BuildDossierHero from './vehicle/BuildDossierHero';
import './vehicle/build-dossier.css';
import BuildChoreography from './vehicle/BuildChoreography';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import apiService from '../services/ApiService';
import { ImageLightbox } from './ImageLightbox';
import CollectibilityPanel from './CollectibilityPanel';
import VehicleBuildViews from './VehicleBuildViews';
import { VehicleHeroGallery } from './vehicle-detail/VehicleHeroGallery';
import type { CollectibilityData } from '../types/collectibility';
import { isGalleryImageType, resolvePublicFileUrl } from '../utils/imageUrls';
import { useSyncedHeroGallery } from './vehicle-detail/useSyncedHeroGallery';
import type { PublicGalleryImage } from './vehicle-detail/vehicleGallerySync';
import { formatCurrency } from '../utils/formatCurrency';
import { useShowcaseMode } from '../hooks/useShowcaseMode';
import { updateMetaTags } from '../utils/metaTags';
import PublicVehiclePageSkeleton from './ui/PublicVehiclePageSkeleton';
import Footer from './Footer';
import ExteriorFinishDisplay from './vehicle/ExteriorFinishDisplay';
import VehicleBuildSpecs from './vehicle/VehicleBuildSpecs';
import ScrollCountUp from './vehicle/ScrollCountUp';
import BuildTimeline from './vehicle/BuildTimeline';
import VehiclePassportStrip from './passport/VehiclePassportStrip';
import VehiclePassportCollectCta from './passport/VehiclePassportCollectCta';
import OwnerOtherBuilds from './vehicle/OwnerOtherBuilds';
import VehicleSectionNav from './vehicle/VehicleSectionNav';
import VehicleRecognitionSection from './vehicle/VehicleRecognitionSection';
import VehicleMaintenanceSection from './vehicle/VehicleMaintenanceSection';
import LimitedDesignationPlate from './limited-designation/LimitedDesignationPlate';
import VehicleSharingModal, { type SharingModalTabId } from './VehicleSharingModal';
import VehicleCertificationPanel from './VehicleCertificationPanel';
import type { VehicleHistoryEntry } from '../types/vehicleHistory';
import { BuiltLogoMark } from './ui/BuiltLogoMark';
import type { VehicleCompletenessResult } from '../utils/vehicleCompleteness';
import { ShareIcon, QrCodeIcon, LinkIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import FavoriteBuildButton from './FavoriteBuildButton';
import LikeBuildButton from './LikeBuildButton';
import BuildWatchButton from './BuildWatchButton';
import BuildEngagementBar from './BuildEngagementBar';
import BuildViewTracker from './BuildViewTracker';
import type { BuildPageExpansion, BuildProfile, ExpansionFeatures } from '../types/buildExpansion';
import {
  ContributorCards,
  DynoHistory,
  FactoryCurrentComparison,
  MediaAlbums,
  OwnerIdentityCard,
  PerformanceRecords,
} from './expansion';
import {
  BuildProfileEditorModal,
  ContributorRequestModal,
  ContributorsManagePanel,
  PerformanceRecordManager,
} from './expansion/owner';
import { Modal } from './ui/Modal';
import { AddModificationModal } from './AddModificationForm';
import { EditModificationModal } from './EditModificationModal';
import EditVehicleModal from './EditVehicleModal';
import { VehicleImageUploadModal } from './VehicleImageUploadModal';
import { ImportModificationsModal } from './ImportModificationsModal';
import { EnhanceModificationsModal } from './EnhanceModificationsModal';
import { AiEditModificationsModal } from './AiEditModificationsModal';
import { SettingsModal } from './SettingsModal';
import { SettingsData } from '../types/settings';
import { mapModificationsWithParent } from '../utils/modifications';
import AdminEditingCaution from './ui/AdminEditingCaution';
import { ImageTitleEditModal } from './vehicle-detail/ImageTitleEditModal';
import type { VehicleDetailModification } from './vehicle-detail/types';
import type { Vehicle } from '../types/vehicle';
import FeatureTip from './onboarding/FeatureTip';
import MaintenancePanel from './manage-build/MaintenancePanel';
import ManageBuildShell, { type ManageBuildActionId } from './manage-build/ManageBuildShell';
import usePremiumEntitlements from '../hooks/usePremiumEntitlements';
import PremiumBadge from './premium/PremiumBadge';
import { Button } from './ui/Button';
import {
  BUILD_MANAGEMENT_ENTRY_LABEL,
  type BuildManagementSectionId,
  isBuildManagementSectionId,
} from '../types/buildManagement';

interface PublicVehicleData {
  vehicle: {
    id: number;
    year: number;
    make: string;
    model: string;
    trim: string;
    color: string;
    exterior_finish_type?: 'paint' | 'wrap' | 'ppf';
    exterior_finish_color?: string | null;
    exterior_finish_brand?: string | null;
    exterior_finish_style?: string | null;
    chassis?: string;
    engine?: string;
    transmission?: string;
    drivetrain?: string;
    build_status?: 'planned' | 'in_progress' | 'complete' | null;
    nickname: string;
    description: string;
    slug: string;
    public_slug?: string | null;
    canonical_slug?: string | null;
    resolved_via_redirect?: boolean;
    is_public?: boolean;
    is_primary?: boolean;
    created_at: string;
    theme_color: string;
    custom_message: string;
  };
  is_owner: boolean;
  can_edit?: boolean;
  is_super_admin_edit?: boolean;
  profileCompletion: VehicleCompletenessResult;
  owner: {
    username: string;
    display_name: string;
    profile_image: string;
  };
  settings: {
    show_modifications: boolean;
    show_performance: boolean;
    show_images: boolean;
    show_contact_info: boolean;
    show_social_links: boolean;
    show_pricing?: boolean;
  };
  modifications: Array<{
    id: string;
    title: string;
    description?: string;
    category: string;
    vendor?: string;
    cost?: number;
    install_date?: string;
    notes?: string;
    part_number?: string;
    power_gain?: number;
    torque_gain?: number;
    weight_change?: number;
    parent_modification_id?: string | number | null;
    parentComponent?: string | null;
    sort_order?: number | null;
    specifications?: string[] | Record<string, unknown> | string | null;
    image_url?: string | null;
    image_id?: number | null;
  }>;
  images: Array<{
    id?: number;
    image_url: string;
    image_type?: string;
    title?: string;
    alt_text?: string;
  }>;
  performance: {
    horsepower?: number | null;
    horsepower_measurement?: 'wheel' | 'crank' | null;
    torque?: number | null;
    redline?: number | null;
  } | null;
  stats: {
    total_views?: number;
    total_shares?: number;
    last_viewed?: string | null;
    view_count?: number;
    favorite_count?: number;
    like_count?: number;
  };
  favorite_count?: number;
  is_favorited?: boolean;
  like_count?: number;
  user_has_liked?: boolean;
  view_count?: number;
  collectibility?: CollectibilityData;
  awards?: Array<{
    id: number;
    title: string;
    placement: string | null;
    event_title: string | null;
    event_slug: string | null;
    published_at: string | null;
  }>;
  maintenance?: {
    summary: {
      visibility: string;
      record_count: number;
      latest_title?: string | null;
      latest_date?: string | null;
      latest_category?: string | null;
    };
    records?: Array<{
      title?: string;
      service_date?: string;
      category?: string;
      mileage?: number | null;
    }>;
  } | null;
  limited_designations?: Array<{
    public_id: string;
    name: string;
    ordinal: number;
    edition_size: number;
    ordinal_label?: string;
    title?: string;
    issuer_label?: string;
    public_blurb?: string | null;
    description?: string | null;
    issued_at?: string;
    authority_class?: string;
    status?: string;
  }>;
  limited_designation_primary?: {
    public_id: string;
    name: string;
    ordinal: number;
    edition_size: number;
    ordinal_label?: string;
    title?: string;
    issuer_label?: string;
    public_blurb?: string | null;
    description?: string | null;
    issued_at?: string;
    authority_class?: string;
    status?: string;
  } | null;
  expansion?: BuildPageExpansion;
  expansion_features?: ExpansionFeatures;
  meta: {
    title: string;
    description: string;
    url: string;
    image: string;
  };
}

/** Approved build-profile accents → CSS hex (WCAG AA vs gray-950 panels). */
const BUILD_ACCENT_HEX: Record<string, string> = {
  orange: '#f97316',
  red: '#ef4444',
  amber: '#f59e0b',
  emerald: '#10b981',
  sky: '#0ea5e9',
  violet: '#8b5cf6',
  rose: '#f43f5e',
  slate: '#94a3b8',
};

const PURPOSE_LABELS: Record<string, string> = {
  street: 'Street',
  daily: 'Daily',
  track: 'Track',
  time_attack: 'Time attack',
  drag: 'Drag',
  drift: 'Drift',
  rally: 'Rally',
  autocross: 'Autocross',
  show: 'Show',
  stance: 'Stance',
  off_road: 'Off-road',
  overland: 'Overland',
  restoration: 'Restoration',
  oem_plus: 'OEM+',
  touring: 'Touring',
  competition: 'Competition',
  mixed_use: 'Mixed use',
};

const COMPLETION_LABELS: Record<string, string> = {
  engine: 'engine',
  transmission: 'transmission',
  drivetrain: 'drivetrain',
  chassis: 'chassis',
  description: 'a description',
  photo: 'a photo',
  modification: 'a modification',
};

const VehiclePage: React.FC = () => {
  const params = useParams<{
    username?: string;
    slug?: string;
    vehicleName?: string;
    vehicleSlug?: string;
  }>();
  const username = params.username;
  const slug = params.slug || params.vehicleSlug || params.vehicleName;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const referrer = searchParams.get('ref');
  const showcaseMode = useShowcaseMode();

  const [vehicleData, setVehicleData] = useState<PublicVehicleData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareModalTab, setShareModalTab] = useState<SharingModalTabId>('settings');
  const [history, setHistory] = useState<VehicleHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const { user, isAuthenticated } = useAuth();
  const { showToast } = useToast();
  const [editableMods, setEditableMods] = useState<VehicleDetailModification[]>([]);
  const [allVendors, setAllVendors] = useState<{ name: string }[]>([]);
  const [isAddModificationModalOpen, setIsAddModificationModalOpen] = useState(false);
  const [addModificationParent, setAddModificationParent] = useState<{
    id: string;
    title: string;
    category?: string;
  } | null>(null);
  const [docNudgeHidden, setDocNudgeHidden] = useState(false);
  const [isEditVehicleModalOpen, setIsEditVehicleModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isImportModificationsModalOpen, setIsImportModificationsModalOpen] = useState(false);
  const [isEnhanceModalOpen, setIsEnhanceModalOpen] = useState(false);
  const [isAiEditModalOpen, setIsAiEditModalOpen] = useState(false);
  const [aiEditPremiumGateOpen, setAiEditPremiumGateOpen] = useState(false);
  const { hasCapability: hasPremiumCapability } = usePremiumEntitlements(isAuthenticated);
  const hasPremiumAi = hasPremiumCapability('premium_ai');
  const [aiBuildEditingEnabled, setAiBuildEditingEnabled] = useState(false);
  const [isImageUploadModalOpen, setIsImageUploadModalOpen] = useState(false);
  const [imageUploadError, setImageUploadError] = useState<string | null>(null);
  const [isEditModificationModalOpen, setIsEditModificationModalOpen] = useState(false);
  const [editingModification, setEditingModification] = useState<VehicleDetailModification | null>(
    null
  );
  const [deleteImageConfirm, setDeleteImageConfirm] = useState<number | null>(null);
  const [imageTitleEditIndex, setImageTitleEditIndex] = useState<number | null>(null);
  const [displaySettings, setDisplaySettings] = useState<SettingsData>({
    showPricing: true,
    showPerformance: true,
  });
  const [liveFavoriteCount, setLiveFavoriteCount] = useState(0);
  const [liveLikeCount, setLiveLikeCount] = useState(0);
  const [liveViewCount, setLiveViewCount] = useState<number | null>(null);
  const [liveIsFavorited, setLiveIsFavorited] = useState(false);
  const [perfManagerOpen, setPerfManagerOpen] = useState(false);
  const [profileEditorOpen, setProfileEditorOpen] = useState(false);
  const [contributorRequestOpen, setContributorRequestOpen] = useState(false);
  const [qrImageUrl, setQrImageUrl] = useState<string | null>(null);
  const [manageBuildOpen, setManageBuildOpen] = useState(false);
  const [privacySavePending, setPrivacySavePending] = useState(false);
  const [manageBuildSection, setManageBuildSection] = useState<BuildManagementSectionId>('profile');
  /** One-time bootstrap from ?manage= — never driven by React Router after that. */
  const manageQueryBootstrappedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { isAiBuildEditingEnabled } = await import('../services/api/clientConfig');
        const config = await apiService.getClientConfig();
        if (!cancelled) {
          setAiBuildEditingEnabled(isAiBuildEditingEnabled(config));
        }
      } catch {
        if (!cancelled) setAiBuildEditingEnabled(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const applyPublicVehicleData = useCallback((data: PublicVehicleData) => {
    setVehicleData(data);
    setLiveFavoriteCount(Number(data.favorite_count ?? data.stats?.favorite_count ?? 0));
    setLiveLikeCount(Number(data.like_count ?? data.stats?.like_count ?? 0));
    setLiveViewCount(
      Number(data.view_count ?? data.stats?.view_count ?? data.stats?.total_views ?? 0)
    );
    setLiveIsFavorited(Boolean(data.is_favorited));
    setDisplaySettings({
      showPricing: data.settings.show_pricing !== false,
      showPerformance: data.settings.show_performance !== false,
    });

    const modCount = data.modifications?.length ?? 0;
    // Lead the share description with the build + owner. Rarity is a
    // secondary detail on Bilt:Spec, so it is intentionally not emphasized here.
    const metaDescription =
      data.meta.description ||
      [
        modCount > 0 ? `${modCount} mods` : null,
        `by ${data.owner.display_name || data.owner.username}`,
      ]
        .filter(Boolean)
        .join(' · ');

    updateMetaTags({
      title:
        data.meta.title.includes('| Bilt:Spec') || data.meta.title.includes('| Built')
          ? data.meta.title.replace('| Built', '| Bilt:Spec')
          : `${data.meta.title} | Bilt:Spec`,
      description: metaDescription,
      image: data.meta.image || `${window.location.origin}/logo512.png`,
      url: data.meta.url,
      type: 'website',
    });
  }, []);

  useEffect(() => {
    const loadVehicleData = async () => {
      if (!username || !slug) {
        setError('Invalid vehicle link');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await apiService.getPublicVehicle(username, slug, referrer || undefined);
        const canonical =
          data.vehicle.canonical_slug || data.vehicle.public_slug || data.vehicle.slug;
        if (
          canonical &&
          slug &&
          canonical !== slug &&
          (data.vehicle.resolved_via_redirect || data.vehicle.public_slug === canonical)
        ) {
          const qs = window.location.search.replace(/^\?/, '');
          navigate(`/${username}/${canonical}${qs ? `?${qs}` : ''}`, { replace: true });
          return;
        }
        applyPublicVehicleData(data);
      } catch (err: unknown) {
        console.error('Failed to load public vehicle data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load vehicle data');
      } finally {
        setLoading(false);
      }
    };

    loadVehicleData();
  }, [username, slug, referrer, navigate, applyPublicVehicleData]);

  const reloadHistory = useCallback(
    async (opts?: { soft?: boolean }) => {
      if (!username || !slug) return;
      const soft = Boolean(opts?.soft);
      try {
        // Soft refresh keeps existing entries mounted so timeline layout does not
        // collapse to a skeleton and yank scroll back to the section top.
        if (!soft) setHistoryLoading(true);
        const entries = await apiService.getVehicleHistory({ username, slug });
        setHistory(entries);
      } catch (err) {
        console.error('Failed to load vehicle activity', err);
        if (!soft) setHistory([]);
      } finally {
        setHistoryLoading(false);
      }
    },
    [username, slug]
  );

  const refreshVehicle = useCallback(async () => {
    if (!username || !slug) return;
    try {
      const data = await apiService.getPublicVehicle(username, slug, referrer || undefined);
      applyPublicVehicleData(data);
    } catch {
      // Best-effort refresh after owner edits. Keep the optimistic page state.
    }
    void reloadHistory({ soft: true });
  }, [username, slug, referrer, applyPublicVehicleData, reloadHistory]);

  useEffect(() => {
    if (!username || !slug) return;
    const timer = window.setTimeout(() => {
      void reloadHistory();
    }, 100);
    return () => window.clearTimeout(timer);
    // Intentionally keyed to route identity only — vehicleData refreshes must not
    // remount the timeline (that caused desktop scroll flicker at #timeline).
  }, [username, slug, reloadHistory]);

  const galleryLabel = vehicleData
    ? `${vehicleData.vehicle.year} ${vehicleData.vehicle.make} ${vehicleData.vehicle.model}`
    : '';
  const commitPublicImages = useCallback((images: PublicGalleryImage[]) => {
    setVehicleData((prev) => (prev ? { ...prev, images } : prev));
  }, []);
  const { galleryImages, heroImages, commitGallery } = useSyncedHeroGallery({
    images: vehicleData?.images,
    label: galleryLabel,
    getFileUrl: (path) => apiService.getFileUrl(path),
    onImagesCommit: commitPublicImages,
  });

  const totalModCost = useMemo(() => {
    const mods = editableMods.length > 0 ? editableMods : vehicleData?.modifications || [];
    return mods.reduce((sum, mod) => sum + (mod.cost || 0), 0);
  }, [vehicleData, editableMods]);

  const refreshVendors = useCallback(async () => {
    try {
      const vendorsData = await apiService.getVendors();
      setAllVendors(vendorsData.map((v: { name: string }) => ({ name: v.name })));
    } catch (err) {
      console.error('Failed to refresh vendors:', err);
    }
  }, []);

  const reloadEditableMods = useCallback(async (vehicleId: number) => {
    const raw = await apiService.getModifications(vehicleId);
    const mapped = mapModificationsWithParent(raw);
    setEditableMods(mapped);
    // Keep public vehicle payload in sync so mod photos survive view switches / refresh paths.
    setVehicleData((prev) => {
      if (!prev || prev.vehicle.id !== vehicleId) return prev;
      return {
        ...prev,
        modifications: mapped.map((mod) => ({
          ...mod,
          image_url: resolvePublicFileUrl(mod.image_url) ?? mod.image_url ?? null,
          image_id: mod.image_id ?? null,
        })),
      };
    });
  }, []);

  const canEdit = !!(
    vehicleData &&
    (vehicleData.can_edit === true ||
      vehicleData.is_super_admin_edit === true ||
      (isAuthenticated && user && vehicleData.is_owner))
  );

  const vehicleId = vehicleData?.vehicle?.id;
  useEffect(() => {
    if (!vehicleId || !canEdit) {
      if (!canEdit) setEditableMods([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await reloadEditableMods(vehicleId);
        await refreshVendors();
      } catch (e) {
        if (!cancelled) console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [vehicleId, canEdit, reloadEditableMods, refreshVendors]);

  useEffect(() => {
    const id = vehicleData?.vehicle?.id;
    if (!id || typeof window === 'undefined') {
      setDocNudgeHidden(false);
      return;
    }
    try {
      setDocNudgeHidden(window.localStorage.getItem(`bilt.hideDocNudge.${id}`) === '1');
    } catch {
      setDocNudgeHidden(false);
    }
  }, [vehicleData?.vehicle?.id]);

  /** Update ?manage= without React Router so the vehicle page does not remount/reload. */
  const syncManageQueryParam = useCallback((open: boolean) => {
    try {
      const url = new URL(window.location.href);
      if (open) {
        if (url.searchParams.get('manage') === '1') return;
        url.searchParams.set('manage', '1');
      } else if (!url.searchParams.has('manage')) {
        return;
      } else {
        url.searchParams.delete('manage');
      }
      const next = `${url.pathname}${url.search}${url.hash}`;
      window.history.replaceState(window.history.state, '', next);
    } catch {
      // Best-effort URL sync only.
    }
  }, []);

  const handleEditModification = (modification: VehicleDetailModification) => {
    setEditingModification(modification);
    setIsEditModificationModalOpen(true);
  };

  const handleDeleteModification = async (modificationId: string) => {
    if (!canEdit || !vehicleData) return;
    try {
      const targetMod = editableMods.find((m) => m.id === modificationId);
      if (!targetMod) return;
      const childMods = editableMods.filter(
        (m) =>
          (m.parent_modification_id &&
            String(m.parent_modification_id) === String(modificationId)) ||
          (m.parentComponent && m.parentComponent === targetMod.title)
      );
      let childPolicy: 'reject' | 'promote' | 'cascade' = 'reject';
      if (childMods.length > 0) {
        const choice = window.prompt(
          `"${targetMod.title}" has ${childMods.length} nested part(s).\n` +
            'Type promote (move children up), cascade (delete all nested), or cancel.',
          'promote'
        );
        if (!choice || choice.toLowerCase() === 'cancel') return;
        const normalized = choice.toLowerCase().trim();
        if (normalized !== 'promote' && normalized !== 'cascade') {
          showToast('Delete cancelled. Enter promote or cascade.', 'error');
          return;
        }
        childPolicy = normalized as 'promote' | 'cascade';
      }
      await apiService.deleteModification(parseInt(modificationId, 10), { childPolicy });
      await reloadEditableMods(vehicleData.vehicle.id);
      void refreshVehicle();
      showToast('Modification deleted', 'success');
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Failed to delete modification.';
      showToast(msg, 'error');
    }
  };

  const handleModificationAdded = async (
    newModification: Record<string, unknown>,
    photoFile?: File | null
  ) => {
    if (!vehicleData) return;
    try {
      let modificationData: Record<string, unknown> = { ...newModification };
      const parentIdFromForm =
        newModification.parent_modification_id != null
          ? Number(newModification.parent_modification_id)
          : NaN;
      if (!Number.isNaN(parentIdFromForm) && parentIdFromForm > 0) {
        modificationData.parent_modification_id = parentIdFromForm;
      } else if (newModification.parentComponent) {
        const parentMod = editableMods.find((mod) => mod.title === newModification.parentComponent);
        if (parentMod) {
          modificationData.parent_modification_id = parseInt(parentMod.id, 10);
        }
      }
      const created = await apiService.createModification(vehicleData.vehicle.id, modificationData);
      const createdId = Number(created?.id);
      if (photoFile && Number.isFinite(createdId) && createdId > 0) {
        try {
          await apiService.uploadVehicleImage(
            vehicleData.vehicle.id,
            photoFile,
            'modification',
            createdId
          );
        } catch {
          showToast('Part added, but photo upload failed. Edit the part to retry.', 'error');
        }
      }
      await reloadEditableMods(vehicleData.vehicle.id);
      setIsAddModificationModalOpen(false);
      setAddModificationParent(null);
      showToast('Modification added', 'success');
      await refreshVendors();
      void refreshVehicle();
    } catch {
      showToast('Failed to add modification. Please try again.', 'error');
    }
  };

  const handleModificationUpdated = async (
    updatedModification: VehicleDetailModification,
    photoFile?: File | null
  ) => {
    if (!vehicleData) return;
    try {
      const payload: Record<string, unknown> = { ...updatedModification };
      if (updatedModification.parentComponent) {
        const parentMod = editableMods.find(
          (mod) => mod.title === updatedModification.parentComponent
        );
        if (parentMod) {
          payload.parent_modification_id = parseInt(parentMod.id, 10);
          payload.cost = 0;
        }
      } else {
        payload.parent_modification_id = null;
      }
      const modId = parseInt(updatedModification.id, 10);
      await apiService.updateModification(modId, payload);
      if (photoFile && Number.isFinite(modId) && modId > 0) {
        try {
          await apiService.uploadVehicleImage(
            vehicleData.vehicle.id,
            photoFile,
            'modification',
            modId
          );
        } catch {
          showToast('Part updated, but photo upload failed. Try again from Edit.', 'error');
        }
      }
      await reloadEditableMods(vehicleData.vehicle.id);
      setIsEditModificationModalOpen(false);
      setEditingModification(null);
      showToast('Modification updated', 'success');
      await refreshVendors();
      void refreshVehicle();
    } catch {
      showToast('Failed to update modification.', 'error');
    }
  };

  const openAddModification = (parent?: VehicleDetailModification | null) => {
    setAddModificationParent(
      parent ? { id: String(parent.id), title: parent.title, category: parent.category } : null
    );
    setIsAddModificationModalOpen(true);
  };

  const handleOrganizeMove = async (args: {
    modificationId: number;
    parentModificationId: number | null;
    category?: string;
    beforeModificationId?: number | null;
    afterModificationId?: number | null;
  }) => {
    if (!vehicleData) return;
    try {
      await apiService.organizeModifications(vehicleData.vehicle.id, {
        action: 'move',
        modification_id: args.modificationId,
        parent_modification_id: args.parentModificationId,
        category: args.category,
        before_modification_id: args.beforeModificationId,
        after_modification_id: args.afterModificationId,
      });
      await reloadEditableMods(vehicleData.vehicle.id);
      showToast('Part moved', 'success');
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Could not move that part.';
      showToast(msg, 'error');
      throw error;
    }
  };

  const handleOrganizeReorder = async (args: {
    parentModificationId: number | null;
    orderedIds: number[];
  }) => {
    if (!vehicleData) return;
    try {
      await apiService.organizeModifications(vehicleData.vehicle.id, {
        action: 'reorder',
        parent_modification_id: args.parentModificationId,
        ordered_ids: args.orderedIds,
      });
      await reloadEditableMods(vehicleData.vehicle.id);
      showToast('Order updated', 'success');
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Could not reorder parts.';
      showToast(msg, 'error');
    }
  };

  const handleOrganizeBulkMove = async (args: {
    modificationIds: number[];
    parentModificationId: number | null;
    category?: string;
    beforeModificationId?: number | null;
    afterModificationId?: number | null;
  }) => {
    if (!vehicleData) return;
    try {
      const data = await apiService.organizeModifications(vehicleData.vehicle.id, {
        action: 'bulk_move',
        modification_ids: args.modificationIds,
        parent_modification_id: args.parentModificationId,
        category: args.category,
        before_modification_id: args.beforeModificationId,
        after_modification_id: args.afterModificationId,
      });
      await reloadEditableMods(vehicleData.vehicle.id);
      const moved = typeof data?.moved === 'number' ? data.moved : args.modificationIds.length;
      showToast(moved === 1 ? 'Moved 1 part' : `Moved ${moved} parts`, 'success');
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Could not move selected parts.';
      showToast(msg, 'error');
      throw error;
    }
  };

  const handleOrganizeBulkCategory = async (args: {
    modificationIds: number[];
    category: string;
    includeNested: boolean;
  }) => {
    if (!vehicleData) return;
    try {
      await apiService.organizeModifications(vehicleData.vehicle.id, {
        action: 'bulk_category',
        modification_ids: args.modificationIds,
        category: args.category,
        include_nested: args.includeNested,
      });
      await reloadEditableMods(vehicleData.vehicle.id);
      showToast('Category updated', 'success');
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Could not change category.';
      showToast(msg, 'error');
    }
  };

  const handleOrganizeBulkDelete = async (args: {
    modificationIds: number[];
    childPolicy: 'promote' | 'cascade';
  }) => {
    if (!vehicleData) return;
    try {
      await apiService.organizeModifications(vehicleData.vehicle.id, {
        action: 'bulk_delete',
        modification_ids: args.modificationIds,
        child_policy: args.childPolicy,
      });
      await reloadEditableMods(vehicleData.vehicle.id);
      showToast('Parts deleted', 'success');
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Could not delete selected parts.';
      showToast(msg, 'error');
    }
  };

  const handleSettingsSave = async (newSettings: SettingsData) => {
    if (!vehicleData) return;
    await apiService.updateVehicleSettings(vehicleData.vehicle.id, {
      show_pricing: newSettings.showPricing ?? true,
      show_performance: newSettings.showPerformance ?? true,
    });
    setDisplaySettings(newSettings);
    setVehicleData((prev) =>
      prev
        ? {
            ...prev,
            settings: {
              ...prev.settings,
              show_pricing: newSettings.showPricing ?? true,
              show_performance: newSettings.showPerformance ?? true,
            },
          }
        : prev
    );
    showToast('Display settings saved', 'success');
  };

  const handleDeleteVehicle = async () => {
    if (!vehicleData || !username) return;
    await apiService.deleteVehicle(vehicleData.vehicle.id);
    showToast('Vehicle deleted', 'success');
    window.location.href = `/${username}`;
  };

  const handleDuplicated = (created: Vehicle) => {
    const nextSlug = created.public_slug || created.slug || '';
    const nextUser = created.owner_username || username;
    showToast('Build duplicated', 'success');
    if (nextUser && nextSlug) {
      navigate(`/${nextUser}/${nextSlug}`);
      return;
    }
    navigate(`/${username}`);
  };

  const handleVehicleUpdated = (updated: Vehicle) => {
    const nextSlug = updated.public_slug || updated.slug || '';
    setVehicleData((prev) =>
      prev
        ? {
            ...prev,
            vehicle: {
              ...prev.vehicle,
              year: updated.year,
              make: updated.make,
              model: updated.model,
              trim: updated.trim || '',
              color: updated.color || '',
              nickname: updated.nickname || '',
              description: updated.description || '',
              chassis: updated.chassis,
              engine: updated.engine,
              transmission: updated.transmission,
              drivetrain: updated.drivetrain,
              build_status: updated.build_status,
              exterior_finish_type: updated.exterior_finish_type ?? undefined,
              exterior_finish_color: updated.exterior_finish_color ?? undefined,
              exterior_finish_brand: updated.exterior_finish_brand ?? undefined,
              exterior_finish_style: updated.exterior_finish_style ?? undefined,
              slug: nextSlug || prev.vehicle.slug,
              public_slug: nextSlug || prev.vehicle.public_slug,
              canonical_slug: nextSlug || prev.vehicle.canonical_slug,
              is_public:
                typeof updated.is_public === 'boolean' ? updated.is_public : prev.vehicle.is_public,
              is_primary:
                typeof updated.is_primary === 'boolean'
                  ? updated.is_primary
                  : prev.vehicle.is_primary,
            },
            performance: {
              horsepower: updated.horsepower ?? updated.performance?.horsepower ?? null,
              horsepower_measurement:
                updated.horsepower_measurement ??
                updated.performance?.horsepower_measurement ??
                null,
              torque: updated.torque ?? updated.performance?.torque ?? null,
              redline: updated.redline ?? updated.performance?.redline ?? null,
            },
          }
        : prev
    );
    if (updated.settings) {
      setDisplaySettings((prev) => ({
        ...prev,
        showPricing: updated.settings?.show_pricing ?? prev.showPricing,
        showPerformance: updated.settings?.show_performance ?? prev.showPerformance,
      }));
    }
    setIsEditVehicleModalOpen(false);
    if (username && nextSlug && slug && nextSlug !== slug) {
      navigate(`/${username}/${nextSlug}`, { replace: true });
      return;
    }
    void refreshVehicle();
  };

  const handleSharingChange = (update: {
    is_public: boolean;
    public_slug?: string | null;
    public_url?: string | null;
    settings: {
      show_modifications?: boolean;
      show_performance?: boolean;
      show_images?: boolean;
      show_contact_info?: boolean;
      show_social_links?: boolean;
    };
  }) => {
    setVehicleData((prev) => {
      if (!prev) return prev;
      // Keep a non-null slug string for owner navigation when privacy clears public_slug.
      const nextPublicSlug =
        update.public_slug !== undefined ? update.public_slug : prev.vehicle.public_slug;
      const nextSlug =
        typeof nextPublicSlug === 'string' && nextPublicSlug.trim() !== ''
          ? nextPublicSlug
          : prev.vehicle.slug || prev.vehicle.canonical_slug || 'build';
      return {
        ...prev,
        vehicle: {
          ...prev.vehicle,
          is_public: update.is_public,
          ...(update.public_slug !== undefined
            ? {
                public_slug: nextPublicSlug ?? null,
                slug: nextSlug,
              }
            : {}),
        },
        settings: {
          ...prev.settings,
          ...(update.settings.show_modifications !== undefined
            ? { show_modifications: update.settings.show_modifications }
            : {}),
          ...(update.settings.show_performance !== undefined
            ? { show_performance: update.settings.show_performance }
            : {}),
          ...(update.settings.show_images !== undefined
            ? { show_images: update.settings.show_images }
            : {}),
          ...(update.settings.show_contact_info !== undefined
            ? { show_contact_info: update.settings.show_contact_info }
            : {}),
          ...(update.settings.show_social_links !== undefined
            ? { show_social_links: update.settings.show_social_links }
            : {}),
        },
      };
    });
    if (update.settings.show_performance !== undefined) {
      setDisplaySettings((prev) => ({
        ...prev,
        showPerformance: update.settings.show_performance !== false,
      }));
    }
  };

  const reloadGallery = useCallback(async () => {
    if (!vehicleData) return;
    const imagesData = await apiService.getVehicleImages(vehicleData.vehicle.id);
    const label = `${vehicleData.vehicle.year} ${vehicleData.vehicle.make} ${vehicleData.vehicle.model}`;
    const next = imagesData
      .filter((img: { image_type?: string }) => isGalleryImageType(img.image_type))
      .map(
        (
          img: { id: number; image_url: string; alt_text?: string; title?: string },
          index: number
        ) => ({
          id: String(img.id),
          src:
            resolvePublicFileUrl(img.image_url) ||
            apiService.getFileUrl(img.image_url) ||
            img.image_url,
          alt: img.alt_text || `${label} photo ${index + 1}`,
          title: img.title || `Photo ${index + 1}`,
        })
      );
    commitGallery(next);
  }, [vehicleData, commitGallery]);

  const handleSaveImageTitle = async (newTitle: string) => {
    if (imageTitleEditIndex === null) return;
    const image = heroImages[imageTitleEditIndex];
    if (!image?.id) throw new Error('Image ID not found');
    await apiService.updateVehicleImage(parseInt(image.id, 10), { title: newTitle });
    const next = [...heroImages];
    next[imageTitleEditIndex] = { ...image, title: newTitle };
    commitGallery(next);
    showToast('Image title updated', 'success');
  };

  const confirmDeleteImage = async () => {
    if (deleteImageConfirm === null) return;
    const index = deleteImageConfirm;
    setDeleteImageConfirm(null);
    try {
      const image = heroImages[index];
      if (!image?.id) throw new Error('Image ID not found');
      await apiService.deleteVehicleImage(parseInt(image.id, 10));
      commitGallery(heroImages.filter((_, i) => i !== index));
      void refreshVehicle();
    } catch {
      showToast('Failed to delete image. Please try again.', 'error');
    }
  };

  const handleMoveImage = async (fromIndex: number, toIndex: number) => {
    if (!canEdit || fromIndex === toIndex) return;
    const next = [...heroImages];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    commitGallery(next);
    try {
      await Promise.all(
        next.map((img, order) =>
          img.id
            ? apiService.updateVehicleImage(parseInt(img.id, 10), { sort_order: order })
            : Promise.resolve()
        )
      );
    } catch {
      showToast('Failed to reorder photos', 'error');
      await reloadGallery();
    }
  };

  const handleImageUploaded = async () => {
    setImageUploadError(null);
    try {
      await reloadGallery();
      showToast('Photo uploaded', 'success');
      void refreshVehicle();
    } catch {
      showToast('Upload succeeded but gallery failed to refresh. Reload the page.', 'error');
    }
  };

  const isOwnerFlag = Boolean(vehicleData?.is_owner);

  // Prefetch branded monochrome QR for owner share tools.
  useEffect(() => {
    if (!isOwnerFlag || showcaseMode || !vehicleData?.vehicle?.id || qrImageUrl) return;
    const shareEnabled = vehicleData.expansion_features?.share_cards_enabled !== false;
    if (!shareEnabled) return;
    let cancelled = false;
    (async () => {
      try {
        const qr = await apiService.generateQRCode(vehicleData.vehicle.id, {
          format: 'svg',
          size: 'large',
        });
        if (cancelled) return;
        const rawPath = qr.qr_code_url || qr.file_path || '';
        const serveMatch = rawPath.match(/serve_qr\.php\?file=[^&]+/);
        const relativePath = serveMatch ? serveMatch[0] : rawPath;
        const url = apiService.getFileUrl(relativePath) || relativePath || null;
        if (url) setQrImageUrl(url);
      } catch {
        // Best-effort prefetch; share tools regenerate on demand.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    isOwnerFlag,
    showcaseMode,
    vehicleData?.vehicle?.id,
    vehicleData?.expansion_features?.share_cards_enabled,
    qrImageUrl,
  ]);

  const openManageBuild = useCallback(
    (section: BuildManagementSectionId = 'profile') => {
      setManageBuildSection(section);
      setManageBuildOpen(true);
      syncManageQueryParam(true);
    },
    [syncManageQueryParam]
  );

  const closeManageBuild = useCallback(() => {
    setManageBuildOpen(false);
    syncManageQueryParam(false);
    void refreshVehicle();
  }, [syncManageQueryParam, refreshVehicle]);

  const trackManageBuildEvent = useCallback(
    (event: string, metadata?: Record<string, unknown>) => {
      if (event !== 'manage_build_opened' && event !== 'manage_build_section_opened') return;
      const vehicleId = vehicleData?.vehicle?.id;
      if (!vehicleId) return;
      void apiService.trackEvent(event, {
        vehicle_id: vehicleId,
        metadata,
      });
    },
    [vehicleData?.vehicle?.id]
  );

  useEffect(() => {
    if (manageQueryBootstrappedRef.current || !canEdit || showcaseMode) return;
    manageQueryBootstrappedRef.current = true;
    const manage = searchParams.get('manage');
    if (!manage) return;
    // `manage=1` opens the shell; legacy `manage=<section>` still works once on load.
    if (manage === '1' || manage === 'true') {
      setManageBuildOpen(true);
      syncManageQueryParam(true);
      return;
    }
    // Passport tools moved to Share → QR & sticker.
    if (manage === 'passport') {
      setShareModalTab('qr');
      setShareModalOpen(true);
      syncManageQueryParam(false);
      return;
    }
    if (isBuildManagementSectionId(manage)) {
      setManageBuildSection(manage);
      setManageBuildOpen(true);
      syncManageQueryParam(true);
    }
  }, [canEdit, showcaseMode, searchParams, syncManageQueryParam]);

  if (loading) {
    return <PublicVehiclePageSkeleton />;
  }

  if (error || !vehicleData) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
        <div className="card-elevated max-w-md w-full p-8 text-center">
          <BuiltLogoMark size="lg" className="mx-auto mb-4" />
          <h1 className="heading-section mb-2">Build not found</h1>
          <p className="text-muted mb-6 text-sm leading-relaxed">
            {error || 'This link may be private or no longer available.'}
          </p>
          <Link to="/discover" className="btn-primary">
            Browse builds
          </Link>
        </div>
      </div>
    );
  }

  const {
    vehicle,
    owner,
    settings,
    modifications: publicMods,
    performance,
    stats,
    collectibility,
    awards,
    is_owner: isOwner,
    profileCompletion,
  } = vehicleData;
  const modifications = canEdit && editableMods.length > 0 ? editableMods : publicMods;
  const hasModCosts = modifications.some((m) => m.cost != null && m.cost > 0);
  const showPricing = displaySettings.showPricing !== false && hasModCosts;
  const activeGallery = canEdit && heroImages.length > 0 ? heroImages : galleryImages;
  const vehicleTitle = `${vehicle.year} ${vehicle.make} ${vehicle.model}${vehicle.trim ? ` ${vehicle.trim}` : ''}`;
  const showGallery = settings.show_images && (activeGallery.length > 0 || canEdit);
  const shareUrl = vehicleData.meta.url;
  const viewCount =
    liveViewCount ?? vehicleData.view_count ?? stats?.view_count ?? stats?.total_views ?? 0;
  const favoriteCount = liveFavoriteCount;
  const isFavorited = liveIsFavorited;
  const likeCount = liveLikeCount;
  const isLiked = Boolean(vehicleData.user_has_liked);
  const expansion = vehicleData.expansion;
  const shareCardsEnabled = vehicleData.expansion_features?.share_cards_enabled !== false;
  const achievements = expansion?.achievements ?? [];
  const milestoneCount = Math.max(
    Array.isArray(awards) ? awards.length : 0,
    achievements.filter((a) => a.status === 'current').length
  );
  const buildSummary = expansion?.build_summary ?? null;
  const buildDescription = (vehicle.description || '').trim();
  /** Prefer owner-entered Edit Vehicle description; fall back to build profile / auto summary. */
  const aboutText = buildDescription || buildSummary || '';
  const buildProfile = expansion?.build_profile ?? null;
  const accentHex =
    (buildProfile?.accent_color && BUILD_ACCENT_HEX[buildProfile.accent_color]) || null;
  const heroTreatment = buildProfile?.hero_treatment === 'light' ? 'light' : 'dark';
  const heroPlacement = buildProfile?.hero_text_placement || 'bottom_left';
  const purposeChips: string[] = [];
  if (buildProfile?.purpose_primary) purposeChips.push(buildProfile.purpose_primary);
  for (const purpose of buildProfile?.purposes ?? []) {
    if (purpose && !purposeChips.includes(purpose)) purposeChips.push(purpose);
  }
  const nonDynoRecords = (expansion?.performance_records ?? []).filter(
    (r) => r.record_type !== 'dyno'
  );

  const showBuildSection =
    (settings.show_modifications || canEdit) && (modifications.length > 0 || canEdit);
  const showPerformanceSection =
    nonDynoRecords.length > 0 || !!expansion?.dyno_summary || !!canEdit;
  const showTimelineSection = history.length > 0 || !!canEdit;
  const showMediaSection = (expansion?.media_albums?.length ?? 0) > 0;
  const publicMaintenance = vehicleData?.maintenance?.summary ?? null;
  const showMaintenanceSection = Boolean(
    publicMaintenance && (publicMaintenance.record_count > 0 || publicMaintenance.latest_title)
  );

  // Core IA: Overview → Recognition → Performance → Build → Media → Contributors → Timeline.
  // Plain array (not useMemo) — this block sits after loading/error early returns.
  const showContributorsSection = (expansion?.contributors?.length ?? 0) > 0;
  const sectionNavItems = [
    { id: 'overview', label: 'Overview' },
    ...(vehicle?.id || canEdit ? [{ id: 'recognition', label: 'Recognition' }] : []),
    ...(showPerformanceSection ? [{ id: 'performance', label: 'Performance' }] : []),
    ...(showBuildSection ? [{ id: 'build', label: 'Build' }] : []),
    ...(showMaintenanceSection ? [{ id: 'maintenance', label: 'Maintenance' }] : []),
    ...(showMediaSection ? [{ id: 'media', label: 'Media' }] : []),
    ...(showContributorsSection ? [{ id: 'contributors', label: 'Contributors' }] : []),
    ...(showTimelineSection ? [{ id: 'timeline', label: 'Timeline' }] : []),
  ];

  const refreshExpansion = () => {
    void refreshVehicle();
  };

  const ensureQrImage = async (): Promise<string | null> => {
    if (qrImageUrl) return qrImageUrl;
    try {
      const qr = await apiService.generateQRCode(vehicle.id, { format: 'svg', size: 'large' });
      const rawPath = qr.qr_code_url || qr.file_path || '';
      const serveMatch = rawPath.match(/serve_qr\.php\?file=[^&]+/);
      const relativePath = serveMatch ? serveMatch[0] : rawPath;
      const url = apiService.getFileUrl(relativePath) || relativePath || null;
      if (url) setQrImageUrl(url);
      return url;
    } catch {
      showToast('Could not generate QR image', 'error');
      return null;
    }
  };

  /** Focused overlays that still need their own dialog (not field forms). */
  const handleManageWorkflow = (action: ManageBuildActionId) => {
    setManageBuildOpen(false);
    syncManageQueryParam(false);
    switch (action) {
      case 'add-part':
        openAddModification();
        break;
      case 'import':
        setIsImportModificationsModalOpen(true);
        break;
      case 'enhance':
        setIsEnhanceModalOpen(true);
        break;
      case 'ai-edit':
        if (aiBuildEditingEnabled) {
          if (hasPremiumAi) {
            setIsAiEditModalOpen(true);
          } else {
            setAiEditPremiumGateOpen(true);
            void apiService.trackEvent('premium_gate_opened', {
              metadata: { feature_key: 'edit_with_ai', surface: 'manage_build_workflow' },
            });
          }
        }
        break;
      case 'add-photos':
        setIsImageUploadModalOpen(true);
        break;
      default:
        break;
    }
  };

  const ownerToolActions =
    canEdit && !showcaseMode
      ? [
          {
            id: 'add-mod',
            label: 'Add part',
            onClick: () => openAddModification(),
          },
          {
            id: 'import-mods',
            label: 'Import build list',
            onClick: () => setIsImportModificationsModalOpen(true),
          },
          ...(modifications.length > 0
            ? [
                {
                  id: 'enhance-mods',
                  label: 'Improve existing parts',
                  onClick: () => setIsEnhanceModalOpen(true),
                },
                ...(aiBuildEditingEnabled
                  ? [
                      {
                        id: 'ai-edit-mods',
                        label: 'Edit with AI',
                        onClick: () => {
                          if (hasPremiumAi) {
                            setIsAiEditModalOpen(true);
                          } else {
                            setAiEditPremiumGateOpen(true);
                            void apiService.trackEvent('premium_gate_opened', {
                              metadata: {
                                feature_key: 'edit_with_ai',
                                surface: 'vehicle_owner_menu',
                              },
                            });
                          }
                        },
                      },
                    ]
                  : []),
              ]
            : []),
          {
            id: 'manage-build',
            label: BUILD_MANAGEMENT_ENTRY_LABEL,
            onClick: () => openManageBuild('profile'),
          },
          {
            id: 'share',
            label: 'Share',
            onClick: () => {
              setShareModalTab('settings');
              setShareModalOpen(true);
            },
          },
        ]
      : [];

  const adminEditingOwnerLabel =
    canEdit && !showcaseMode && vehicleData?.is_super_admin_edit && !isOwner
      ? `@${owner.username}`
      : null;

  const applyBuildProfile = (profile: BuildProfile | null) => {
    setVehicleData((prev) =>
      prev
        ? {
            ...prev,
            expansion: {
              ...(prev.expansion || {
                build_profile: null,
                build_summary: null,
                stats_strip: [],
                power_to_weight: null,
                factory_comparison: null,
                achievements: [],
                documentation_score: null,
                performance_records: [],
                dyno_summary: null,
                media_albums: [],
                media_links: [],
                contributors: [],
              }),
              build_profile: profile,
              build_summary: profile?.summary || prev.expansion?.build_summary || null,
            },
          }
        : prev
    );
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable (e.g. insecure context); silently ignore.
    }
  };

  const handleShare = async () => {
    // Prefer the native share sheet on supporting devices; otherwise copy the link.
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({
          title: vehicleData.meta.title,
          text: vehicleData.meta.description,
          url: shareUrl,
        });
        return;
      } catch {
        // User cancelled or share failed — fall back to copying the link.
      }
    }
    await handleCopyLink();
  };

  const galleryBlock = showGallery ? (
    <VehicleHeroGallery
      images={activeGallery}
      isOwner={canEdit}
      onImageClick={setLightboxIndex}
      onEditImage={canEdit ? (index) => setImageTitleEditIndex(index) : () => undefined}
      onDeleteImage={canEdit ? (index) => setDeleteImageConfirm(index) : () => undefined}
      onMoveImage={canEdit ? handleMoveImage : () => undefined}
      onAddImage={canEdit ? () => setIsImageUploadModalOpen(true) : () => undefined}
      showcaseMode={showcaseMode}
      showcasePanel={showcaseMode}
      embedded={!showcaseMode}
    />
  ) : null;

  const documentingNudge =
    isOwner && !showcaseMode && !profileCompletion.completed && !docNudgeHidden ? (
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
        <div className="flex items-center justify-between gap-3 mb-2">
          <p className="text-sm font-medium text-gray-200">Keep documenting this build</p>
          <div className="flex items-center gap-2 shrink-0">
            <span className="font-mono text-xs text-gray-300 font-semibold tabular-nums">
              {profileCompletion.percent}%
            </span>
            <button
              type="button"
              className="text-xs text-gray-500 hover:text-gray-300 min-h-[36px] px-2 rounded-md
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50"
              onClick={() => {
                setDocNudgeHidden(true);
                const id = vehicle.id;
                try {
                  if (id) window.localStorage.setItem(`bilt.hideDocNudge.${id}`, '1');
                } catch {
                  // ignore
                }
              }}
            >
              Hide
            </button>
          </div>
        </div>
        <div className="h-1.5 w-full rounded-full bg-white/[0.06] overflow-hidden">
          <div
            className="h-full rounded-full bg-orange-500/80 transition-all"
            style={{
              width: `${profileCompletion.percent}%`,
              ...(accentHex ? { backgroundColor: accentHex } : {}),
            }}
          />
        </div>
        {profileCompletion.missing.length > 0 && (
          <p className="text-xs text-gray-400 mt-2">
            Only you can see this. Add{' '}
            {profileCompletion.missing
              .slice(0, 3)
              .map((m) => COMPLETION_LABELS[m] || m)
              .join(', ')}
            {profileCompletion.missing.length > 3 ? '…' : ''} next.
          </p>
        )}
        <button
          type="button"
          onClick={() => openManageBuild('profile')}
          className="mt-3 text-sm font-medium text-orange-300 hover:text-orange-200 min-h-[44px]"
        >
          {BUILD_MANAGEMENT_ENTRY_LABEL}
        </button>
      </div>
    ) : null;

  const profileCompletionSection =
    isOwner && !showcaseMode && documentingNudge ? (
      <div className="mb-1">{documentingNudge}</div>
    ) : null;

  const mainSections = (
    <>
      {canEdit && !showcaseMode && activeGallery.length === 0 ? (
        <FeatureTip tipId="hero_photo" className="mb-4" enabled={canEdit}>
          A clear primary photo is the first thing people see on your public build. Add one when you
          are ready.
        </FeatureTip>
      ) : null}

      {canEdit && !showcaseMode && activeGallery.length > 0 && modifications.length === 0 ? (
        <FeatureTip tipId="import_mods" className="mb-4" enabled={canEdit}>
          Paste the list you already keep in Notes, or upload a spreadsheet. Bilt:Spec will organize
          it for you to review.
        </FeatureTip>
      ) : null}

      <div
        className={`build-dossier-summary grid grid-cols-1 gap-5 sm:gap-6 ${
          vehicle.engine ||
          vehicle.transmission ||
          vehicle.drivetrain ||
          vehicle.chassis ||
          vehicle.build_status
            ? 'lg:grid-cols-2 lg:gap-8 lg:items-stretch'
            : ''
        }`}
      >
        <section
          id="overview"
          className={`section-surface h-full scroll-mt-24 rounded-2xl ${
            showcaseMode ? 'p-6 sm:p-8' : 'p-5 sm:p-7'
          }`}
          style={
            accentHex
              ? ({
                  ['--section-color' as string]: accentHex,
                  ['--section-border' as string]: `${accentHex}55`,
                  ['--section-tint' as string]: `${accentHex}12`,
                  borderColor: `${accentHex}55`,
                  backgroundColor: `${accentHex}12`,
                } as React.CSSProperties)
              : undefined
          }
        >
          <div className="flex flex-col gap-6 h-full">
            <div className="min-w-0">
              <div className="build-section-intro build-profile-intro">
                <p className="kicker mb-3" style={accentHex ? { color: accentHex } : undefined}>
                  Build profile
                </p>
                <h1
                  className={`font-display font-semibold leading-[1.05] tracking-tight text-white ${
                    showcaseMode
                      ? 'text-3xl sm:text-4xl md:text-5xl'
                      : 'text-2xl sm:text-3xl md:text-[2.5rem]'
                  }`}
                >
                  {vehicle.year}{' '}
                  <span className="block sm:inline mt-0.5 sm:mt-0">
                    {vehicle.make} {vehicle.model}
                  </span>
                  {vehicle.trim && (
                    <span className="block text-lg sm:text-xl md:text-2xl font-normal text-gray-400 mt-1.5 tracking-normal">
                      {vehicle.trim}
                    </span>
                  )}
                </h1>
                {vehicle.nickname && (
                  <p
                    className={`mt-4 font-display italic font-medium ${
                      accentHex ? '' : 'text-orange-200/90'
                    } ${showcaseMode ? 'text-xl sm:text-2xl' : 'text-lg sm:text-xl'}`}
                    style={accentHex ? { color: accentHex } : undefined}
                  >
                    &ldquo;{vehicle.nickname}&rdquo;
                  </p>
                )}
                {(vehicleData.limited_designation_primary ||
                  vehicleData.limited_designations?.[0]) && (
                  <div className="mt-4">
                    <LimitedDesignationPlate
                      designation={
                        vehicleData.limited_designation_primary ||
                        vehicleData.limited_designations![0]
                      }
                      size="full"
                    />
                  </div>
                )}
                {(purposeChips.length > 0 || buildProfile?.build_stage) && (
                  <ul
                    className="mt-3 flex flex-wrap gap-2 list-none"
                    aria-label="Build purpose and stage"
                  >
                    {buildProfile?.build_stage ? (
                      <li
                        className="rounded-lg border border-white/15 bg-white/[0.04] px-2.5 py-1 text-xs text-gray-200 capitalize"
                        style={
                          accentHex
                            ? {
                                borderColor: `${accentHex}88`,
                                color: accentHex,
                                backgroundColor: `${accentHex}18`,
                              }
                            : undefined
                        }
                      >
                        {String(buildProfile.build_stage).replace(/_/g, ' ')}
                      </li>
                    ) : null}
                    {purposeChips.map((purpose) => (
                      <li
                        key={purpose}
                        className="rounded-lg border border-orange-500/25 bg-orange-500/[0.07] px-2.5 py-1 text-xs text-orange-200/90"
                        style={
                          accentHex
                            ? {
                                borderColor: `${accentHex}66`,
                                color: accentHex,
                                backgroundColor: `${accentHex}14`,
                              }
                            : undefined
                        }
                      >
                        {PURPOSE_LABELS[purpose] || purpose}
                      </li>
                    ))}
                  </ul>
                )}
                {aboutText ? (
                  <div className="mt-4 max-w-2xl">
                    {buildDescription ? (
                      <p className="spec-label text-zinc-500 mb-1.5">About this build</p>
                    ) : null}
                    <p
                      className={`text-gray-400 leading-relaxed ${
                        showcaseMode ? 'text-base sm:text-lg' : 'text-sm sm:text-base'
                      }`}
                    >
                      {aboutText}
                    </p>
                  </div>
                ) : null}
              </div>
              <ExteriorFinishDisplay data={vehicle} className="mt-3 max-w-xl" />
            </div>

            <div className="flex items-center gap-3 pt-4 border-t border-white/[0.06]">
              {owner.profile_image ? (
                <img
                  src={owner.profile_image}
                  alt=""
                  className="w-11 h-11 rounded-full object-cover ring-1 ring-white/[0.08]"
                />
              ) : (
                <span className="w-11 h-11 rounded-full bg-[#111] flex items-center justify-center text-sm font-semibold text-gray-300 ring-1 ring-white/[0.08]">
                  {(owner.display_name || owner.username).charAt(0).toUpperCase()}
                </span>
              )}
              <div className="min-w-0">
                <p className="spec-label">Builder</p>
                <Link
                  to={`/${owner.username}`}
                  className="text-sm font-medium text-white hover:text-orange-200 transition-colors truncate block"
                >
                  {owner.display_name || owner.username}
                </Link>
                <p className="font-mono text-xs text-gray-500">@{owner.username}</p>
              </div>
            </div>

            {displaySettings.showPerformance !== false && performance && (
              <div
                className={`stat-ledger grid grid-cols-3 divide-x divide-white/[0.06]/60 ${showcaseMode ? 'max-w-md' : ''}`}
              >
                {performance.horsepower != null && (
                  <div className="px-3 py-4 sm:py-5 text-center">
                    <div
                      className={`build-dossier-stat-value build-dossier-stat-value--power font-display font-semibold tracking-tight text-white tabular-nums leading-none ${showcaseMode ? 'text-3xl' : 'text-2xl sm:text-[1.75rem]'}`}
                    >
                      <ScrollCountUp
                        value={performance.horsepower}
                        className="build-dossier-count-up"
                        aria-label={`${performance.horsepower.toLocaleString()} ${performance.horsepower_measurement === 'wheel' ? 'wheel horsepower' : 'horsepower'}`}
                      />
                    </div>
                    <div className="spec-label mt-2">
                      {performance.horsepower_measurement === 'wheel' ? 'WHP' : 'HP'}
                    </div>
                  </div>
                )}
                {performance.torque != null && (
                  <div className="px-3 py-4 sm:py-5 text-center">
                    <div
                      className={`build-dossier-stat-value build-dossier-stat-value--torque font-display font-semibold tracking-tight text-white tabular-nums leading-none ${showcaseMode ? 'text-3xl' : 'text-2xl sm:text-[1.75rem]'}`}
                    >
                      <ScrollCountUp
                        value={performance.torque}
                        className="build-dossier-count-up"
                        aria-label={`${performance.torque.toLocaleString()} pound feet of torque`}
                      />
                    </div>
                    <div className="spec-label mt-2">Torque</div>
                  </div>
                )}
                {performance.redline != null && (
                  <div className="px-3 py-4 sm:py-5 text-center">
                    <div
                      className={`build-dossier-stat-value build-dossier-stat-value--redline font-display font-semibold tracking-tight text-white tabular-nums leading-none ${showcaseMode ? 'text-3xl' : 'text-2xl sm:text-[1.75rem]'}`}
                    >
                      <ScrollCountUp
                        value={performance.redline}
                        className="build-dossier-count-up"
                        aria-label={`${performance.redline.toLocaleString()} RPM redline`}
                      />
                    </div>
                    <div className="spec-label mt-2">Redline</div>
                  </div>
                )}
              </div>
            )}

            {!showcaseMode && (
              <div className="flex flex-col gap-2 sm:max-w-md mt-auto">
                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={handleShare}
                    className="btn-primary flex-1 min-w-[8rem]"
                  >
                    <ShareIcon className="w-4 h-4" />
                    Share build
                  </button>
                  <button
                    type="button"
                    onClick={handleCopyLink}
                    aria-label="Copy link"
                    title="Copy link"
                    className="btn-secondary min-w-[44px] px-3"
                  >
                    {copied ? (
                      <CheckIcon className="w-4 h-4 text-emerald-400" />
                    ) : (
                      <LinkIcon className="w-4 h-4" />
                    )}
                  </button>
                  {
                    <>
                      <LikeBuildButton
                        vehicleId={vehicle.id}
                        initialLiked={isLiked}
                        initialLikeCount={likeCount}
                        size="md"
                        showCount
                        onLikeChange={(_liked, nextCount) => setLiveLikeCount(nextCount)}
                      />
                      <FavoriteBuildButton
                        vehicleId={vehicle.id}
                        initialFavorited={isFavorited}
                        initialFavoriteCount={favoriteCount}
                        size="md"
                        showCount
                        onFavoriteChange={(favorited, nextCount) => {
                          setLiveIsFavorited(favorited);
                          setLiveFavoriteCount(nextCount);
                        }}
                      />
                      {!isOwner && (
                        <BuildWatchButton vehicleId={vehicle.id} isFavorited={isFavorited} />
                      )}
                    </>
                  }
                  {isOwner && (
                    <button
                      type="button"
                      onClick={() => {
                        setShareModalTab('qr');
                        setShareModalOpen(true);
                      }}
                      aria-label="QR code and sticker"
                      title="QR & sticker"
                      data-testid="vehicle-share-open"
                      className="btn-secondary min-w-[44px] px-3"
                    >
                      <QrCodeIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {copied && (
                  <p className="text-xs text-emerald-400/90" role="status">
                    Link copied
                  </p>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Build specs — technical spec grid (Bilt identity fields). */}
        <VehicleBuildSpecs
          chassis={vehicle.chassis}
          engine={vehicle.engine}
          transmission={vehicle.transmission}
          drivetrain={vehicle.drivetrain}
          buildStatus={vehicle.build_status}
          className={`build-dossier-specs h-full border border-white/[0.06] bg-[#0c0c0c]/80 ${showcaseMode ? 'p-6 sm:p-8' : 'p-5 sm:p-6'}`}
        />
      </div>

      {vehicle.custom_message && (
        <section
          className={`build-dossier-message section-surface rounded-xl ${
            showcaseMode ? 'px-6 py-5 sm:px-8 sm:py-6' : 'px-5 py-4 sm:px-6 sm:py-5'
          }`}
          style={
            {
              ['--section-color' as string]: accentHex || '#F97316',
              ['--section-border' as string]: `${accentHex || '#F97316'}55`,
              ['--section-tint' as string]: `${accentHex || '#F97316'}12`,
              borderColor: `${accentHex || '#F97316'}55`,
              backgroundColor: `${accentHex || '#F97316'}12`,
            } as React.CSSProperties
          }
        >
          <p className="kicker mb-2">Message from {owner.display_name || owner.username}</p>
          <p
            className={`text-gray-200 leading-relaxed ${showcaseMode ? 'text-base sm:text-lg' : 'text-sm sm:text-base'}`}
          >
            {vehicle.custom_message}
          </p>
        </section>
      )}

      {vehicle?.id ? (
        <div id="recognition" className="scroll-mt-24">
          <VehicleRecognitionSection
            vehicleId={vehicle.id}
            isOwner={!!canEdit}
            awards={awards || []}
            achievements={achievements}
            limitedDesignations={vehicleData.limited_designations || []}
          />
        </div>
      ) : null}

      {showPerformanceSection ? (
        <section
          id="performance"
          className="space-y-4 scroll-mt-24"
          aria-label="Performance"
          onFocus={() => {
            apiService.trackEvent('performance_record_opened', { vehicle_id: vehicle.id });
          }}
        >
          {nonDynoRecords.length > 0 || expansion?.dyno_summary ? (
            <>
              {nonDynoRecords.length > 0 ? <PerformanceRecords records={nonDynoRecords} /> : null}
              {expansion?.dyno_summary ? <DynoHistory summary={expansion.dyno_summary} /> : null}
            </>
          ) : canEdit ? (
            <div className="rounded-2xl border border-dashed border-white/[0.08] bg-[#0c0c0c]/40 p-5 sm:p-6">
              <p className="kicker mb-2" style={accentHex ? { color: accentHex } : undefined}>
                Performance
              </p>
              <h2 className="heading-section text-xl mb-2">No records yet</h2>
              <p className="text-sm text-gray-400 mb-4 max-w-md">
                Add dyno, track, or drag results so visitors can see how this build performs.
              </p>
              <button
                type="button"
                className="btn-secondary btn-sm min-h-[44px]"
                onClick={() => setPerfManagerOpen(true)}
              >
                Add performance records
              </button>
            </div>
          ) : null}
        </section>
      ) : null}

      {expansion?.factory_comparison ? (
        <div
          onFocus={() => {
            apiService.trackEvent('factory_comparison_opened', { vehicle_id: vehicle.id });
          }}
        >
          <FactoryCurrentComparison comparison={expansion.factory_comparison} />
        </div>
      ) : null}

      {showBuildSection && (
        <section className="space-y-4 md:space-y-5 scroll-mt-24" id="build">
          <div className="build-section-intro border-b border-white/[0.06] pb-4">
            <p className="kicker mb-2" style={accentHex ? { color: accentHex } : undefined}>
              Documented parts
            </p>
            <h2 className="heading-section">
              Build
            </h2>
            <p
              className={`font-mono text-xs text-gray-500 mt-1.5 tabular-nums ${showcaseMode ? 'text-sm' : ''}`}
            >
              <ScrollCountUp
                value={modifications.length}
                className="build-dossier-count-up"
                aria-label={`${modifications.length.toLocaleString()} documented part${modifications.length === 1 ? '' : 's'}`}
              />{' '}
              documented part{modifications.length === 1 ? '' : 's'}
              {showPricing && totalModCost > 0 && (
                <span className="text-gray-600"> · {formatCurrency(totalModCost)} invested</span>
              )}
            </p>
          </div>
          <VehicleBuildViews
            modifications={modifications.map((mod) => ({
              id: mod.id,
              title: mod.title,
              description: mod.description ?? '',
              category: mod.category,
              vendor: mod.vendor ?? '',
              cost: mod.cost,
              install_date: mod.install_date,
              notes: mod.notes,
              parent_modification_id: mod.parent_modification_id,
              parentComponent: mod.parentComponent,
              specifications: mod.specifications,
              sort_order: mod.sort_order,
              image_url: resolvePublicFileUrl(mod.image_url) ?? mod.image_url ?? null,
              image_id: mod.image_id ?? null,
            }))}
            showPricing={showPricing}
            compact={showcaseMode}
            syncUrl={!showcaseMode}
            autoCarShow={showcaseMode}
            defaultCollapseLarge
            canEdit={canEdit}
            vehicleLabel={vehicle.nickname || `${vehicle.year} ${vehicle.make} ${vehicle.model}`}
            vehicleImageUrl={settings.show_images ? activeGallery[0]?.src : undefined}
            onEditModification={canEdit ? handleEditModification : undefined}
            onDeleteModification={canEdit ? handleDeleteModification : undefined}
            onAddChildModification={canEdit ? (parent) => openAddModification(parent) : undefined}
            onOrganizeMove={canEdit ? handleOrganizeMove : undefined}
            onOrganizeReorder={canEdit ? handleOrganizeReorder : undefined}
            onOrganizeBulkMove={canEdit ? handleOrganizeBulkMove : undefined}
            onOrganizeBulkCategory={canEdit ? handleOrganizeBulkCategory : undefined}
            onOrganizeBulkDelete={canEdit ? handleOrganizeBulkDelete : undefined}
            emptyActions={
              canEdit && !showcaseMode ? (
                <div className="flex flex-wrap justify-center gap-3">
                  <button
                    type="button"
                    onClick={() => openAddModification()}
                    className="btn-primary"
                  >
                    Add modification
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsImportModificationsModalOpen(true)}
                    className="btn-secondary"
                  >
                    Import build list
                  </button>
                </div>
              ) : null
            }
          />
        </section>
      )}

      {showMaintenanceSection && publicMaintenance ? (
        <VehicleMaintenanceSection
          summary={publicMaintenance}
          records={vehicleData?.maintenance?.records ?? []}
          accentHex={accentHex ?? undefined}
        />
      ) : null}

      {settings.show_images && activeGallery.length === 0 && !canEdit && (
        <section className="rounded-2xl border border-dashed border-white/[0.08] bg-white/[0.02] py-12 text-center">
          <p className="text-gray-500 text-sm">No photos shared for this build yet.</p>
        </section>
      )}

      {(expansion?.media_albums?.length ?? 0) > 0 ? (
        <div id="media" className="scroll-mt-24">
          <MediaAlbums
            albums={expansion!.media_albums}
            vehicleId={vehicle.id}
            onOpenAlbum={() => {
              apiService.trackEvent('media_album_opened', { vehicle_id: vehicle.id });
            }}
          />
        </div>
      ) : null}

      <VehiclePassportStrip vehicleId={vehicle.id} />
      {username && slug ? (
        <VehiclePassportCollectCta
          vehicleId={vehicle.id}
          shareUrl={`/${username}/${slug}`}
          isOwner={Boolean(isOwner || canEdit)}
        />
      ) : null}

      {/* Core IA: Overview → Recognition → Performance → Build → Media → Contributors → Timeline */}
      {(expansion?.contributors?.length ?? 0) > 0 ? (
        <div
          id="contributors"
          className="scroll-mt-24"
          onFocus={() => {
            apiService.trackEvent('contributor_opened', { vehicle_id: vehicle.id });
          }}
        >
          <ContributorCards contributors={expansion!.contributors} />
        </div>
      ) : null}

      {showTimelineSection ? (
        <div id="timeline" className="scroll-mt-24">
          <BuildTimeline entries={history} loading={historyLoading} />
        </div>
      ) : null}

      {buildProfile?.owner_card_enabled ? (
        <div
          onFocus={() => {
            apiService.trackEvent('owner_card_opened', { vehicle_id: vehicle.id });
          }}
        >
          <OwnerIdentityCard
            card={buildProfile.owner_card}
            username={owner.username}
            avatarUrl={owner.profile_image || null}
            enabled
          />
        </div>
      ) : null}

      {vehicle?.id && owner?.username && (vehicle as any).is_public !== false ? (
        <OwnerOtherBuilds
          ownerUsername={owner.username}
          currentVehicleId={vehicle.id}
          ownerDisplayName={owner.display_name || owner.username}
        />
      ) : null}

      {!showcaseMode && !canEdit && (
        <section className="relative overflow-hidden rounded-2xl ring-1 ring-white/5 bg-gradient-to-b from-[#0a0a0a]/70 to-[#0a0a0a]/60 p-8 sm:p-12 text-center">
          <BuiltLogoMark size="md" className="mx-auto mb-4" />
          <p className="font-display text-xl sm:text-2xl font-semibold tracking-tight text-white max-w-md mx-auto mb-2">
            Every build has a story.
          </p>
          <p className="text-muted text-sm sm:text-base max-w-md mx-auto mb-7 leading-relaxed">
            Start yours on Bilt:Spec.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-sm mx-auto sm:max-w-none">
            <Link to="/register" className="btn-primary btn-lg sm:px-8">
              Start your garage
            </Link>
            <Link to="/events" className="btn-ghost btn-lg">
              Browse events
            </Link>
          </div>
        </section>
      )}
    </>
  );

  const heroMasthead = !showcaseMode ? (
    <div
      className={`build-dossier-masthead build-hero-callout flex flex-col gap-3 px-4 sm:px-5 py-4 ${
        heroPlacement === 'bottom_center'
          ? 'items-center text-center sm:flex-col'
          : 'sm:flex-row sm:items-end sm:justify-between'
      }`}
    >
      <div className="min-w-0">
        <p
          className="text-[11px] font-semibold uppercase tracking-[0.14em] mb-1 text-gray-400"
          style={accentHex ? { color: accentHex } : undefined}
        >
          {vehicle.year} {vehicle.make}
        </p>
        <p className="showcase-identity-title font-display font-semibold tracking-tight leading-tight text-xl sm:text-2xl text-white">
          {vehicle.nickname || `${vehicle.model}${vehicle.trim ? ` ${vehicle.trim}` : ''}`}
        </p>
      </div>
      <div className="showcase-identity-context">
        <Link to={`/${owner.username}`} className="showcase-identity-owner">
          Built by {owner.display_name || owner.username}
        </Link>
        {showBuildSection ? (
          <button
            type="button"
            className="showcase-explore-cta"
            onClick={() => {
              const section = document.getElementById('build');
              if (section)
                window.scrollTo({
                  top: section.getBoundingClientRect().top + window.scrollY - 88,
                  behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
                    ? 'auto'
                    : 'smooth',
                });
            }}
          >
            Explore build <span aria-hidden>↗</span>
          </button>
        ) : null}
      </div>
      <div
        className={`flex flex-wrap items-center gap-2 sm:gap-3 ${
          heroPlacement === 'bottom_center' ? 'justify-center' : ''
        }`}
      >
        <LikeBuildButton
          vehicleId={vehicle.id}
          initialLiked={isLiked}
          initialLikeCount={likeCount}
          showCount={false}
          onLikeChange={(_liked, count) => setLiveLikeCount(count)}
        />
        <FavoriteBuildButton
          vehicleId={vehicle.id}
          initialFavorited={isFavorited}
          initialFavoriteCount={favoriteCount}
          showCount={false}
          onFavoriteChange={(favorited, count) => {
            setLiveIsFavorited(favorited);
            setLiveFavoriteCount(count);
          }}
        />
        <BuildEngagementBar
          favoriteCount={favoriteCount}
          likeCount={likeCount}
          viewCount={viewCount}
          milestoneCount={milestoneCount > 0 ? milestoneCount : null}
          viewTitle={
            isOwner
              ? `${viewCount.toLocaleString()} visitor views (your own visits aren’t counted)`
              : null
          }
        />
        {collectibility ? (
          <CollectibilityPanel data={collectibility} compact defaultExpanded={false} />
        ) : null}
      </div>
    </div>
  ) : null;

  const galleryAndMain = (
    <div className="build-dossier-document flex flex-col min-w-0">
      <BuildChoreography />
      {profileCompletionSection ? (
        <div className="mb-4 sm:mb-5">{profileCompletionSection}</div>
      ) : null}
      <main className="build-dossier-sections space-y-5 sm:space-y-6 md:space-y-8">
        {mainSections}
      </main>
    </div>
  );

  // overflow-x-clip (not hidden): clips horizontal bleed without breaking sticky sidebar.
  const pageRootClass = `page relative min-h-[100dvh] overflow-x-clip ${showcaseMode ? 'showcase-page' : 'build-dossier'} ${accentHex ? 'page-has-build-accent' : ''}`;
  const pageRootStyle: React.CSSProperties | undefined = accentHex
    ? ({
        ['--build-accent' as string]: accentHex,
      } as React.CSSProperties)
    : undefined;

  const pageBody = (
    <div
      className={pageRootClass}
      style={pageRootStyle}
      data-hero-treatment={heroTreatment}
      data-hero-placement={heroPlacement}
    >
      {!showcaseMode ? (
        <>
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background: accentHex
                ? `radial-gradient(ellipse at top left, ${accentHex}18, transparent 55%)`
                : 'radial-gradient(ellipse at top left, rgba(249,115,22,0.07), transparent 55%)',
            }}
            aria-hidden
          />
          <div className="film-grain opacity-[0.035]" aria-hidden />
        </>
      ) : null}
      {showcaseMode ? (
        <header className="showcase-header">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-meta text-orange-400/80 mb-0.5">Bilt:Spec build</p>
              <p className="text-sm sm:text-base font-semibold text-white truncate">
                {vehicleTitle}
              </p>
            </div>
            <Link
              to={`/${owner.username}`}
              className="flex items-center gap-2 shrink-0 text-sm text-gray-300 hover:text-white transition-colors"
            >
              {owner.profile_image ? (
                <img
                  src={owner.profile_image}
                  alt=""
                  className="w-9 h-9 rounded-full object-cover ring-1 ring-white/[0.12]"
                />
              ) : (
                <span className="w-9 h-9 rounded-full bg-[#111] flex items-center justify-center text-xs font-bold text-orange-300">
                  {(owner.display_name || owner.username).charAt(0).toUpperCase()}
                </span>
              )}
              <span className="hidden sm:inline truncate max-w-[120px]">@{owner.username}</span>
            </Link>
          </div>
        </header>
      ) : null}

      {showcaseMode && showGallery ? (
        <div className="showcase-split">
          <div className="showcase-split-gallery">{galleryBlock}</div>
          <div className="showcase-split-content">
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-6 sm:space-y-8">
              {mainSections}
            </main>
          </div>
        </div>
      ) : (
        <div className="build-dossier-layout max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-5 sm:py-6 md:py-8">
          {showGallery ? (
            showcaseMode ? (
              galleryBlock
            ) : (
              <BuildDossierHero
                media={galleryBlock}
                masthead={heroMasthead}
                titleFirst={heroPlacement === 'top_left'}
              />
            )
          ) : null}
          {!showGallery && !showcaseMode ? (
            <div className="mb-5 rounded-2xl border border-white/[0.06] bg-[#0c0c0c]/70 overflow-hidden">
              {heroMasthead}
            </div>
          ) : null}
          {showcaseMode ? (
            galleryAndMain
          ) : (
            <VehicleSectionNav
              items={sectionNavItems}
              ownerTools={ownerToolActions}
              ownerToolsExtra={
                adminEditingOwnerLabel ? (
                  <div className="px-1 pt-2">
                    <AdminEditingCaution
                      contentLabel="vehicle"
                      ownerLabel={adminEditingOwnerLabel}
                      className="w-full [&_button]:w-full [&_button]:justify-center"
                    />
                  </div>
                ) : null
              }
              accentColor={accentHex}
              collectible={{
                source: {
                  ...vehicle,
                  owner_username: owner.username,
                  modification_count: modifications.length,
                  image_url: activeGallery[0]?.src || undefined,
                },
                isOwner: isOwner,
                vehicleId: vehicle.id,
                ownerUsername: owner.username,
                publicSlug: vehicle.public_slug || vehicle.slug,
                story: vehicle.description,
                title: vehicle.nickname || `${vehicle.year} ${vehicle.make} ${vehicle.model}`,
                year: vehicle.year,
                make: vehicle.make,
                model: vehicle.model,
                trim: vehicle.trim,
                imageUrl: activeGallery[0]?.src || null,
                modificationCount: modifications.length,
                certificationStatus:
                  (vehicle as { certification_status?: string | null }).certification_status ??
                  null,
                rarityStars: collectibility?.base_rarity_stars ?? null,
              }}
            >
              {galleryAndMain}
            </VehicleSectionNav>
          )}
        </div>
      )}

      <ImageLightbox
        isOpen={lightboxIndex !== null}
        onClose={() => setLightboxIndex(null)}
        images={activeGallery.map((img) => ({
          src: img.src,
          alt: img.alt,
          title: img.title,
        }))}
        currentIndex={lightboxIndex ?? 0}
        onNavigate={setLightboxIndex}
      />

      {canEdit && (
        <>
          <ManageBuildShell
            isOpen={manageBuildOpen}
            onClose={closeManageBuild}
            blockClose={privacySavePending}
            initialSection={manageBuildSection}
            vehicleTitle={vehicle.nickname || `${vehicle.year} ${vehicle.make} ${vehicle.model}`}
            onTrack={trackManageBuildEvent}
            accentColor={accentHex}
            sectionPanels={{
              profile: (
                <div className="space-y-4 min-w-0">
                  <p className="text-sm text-gray-400 break-words">
                    Each block saves on its own. Press the Save button under the block you edited.
                  </p>
                  <button
                    type="button"
                    className="btn-secondary w-full justify-center min-h-[44px]"
                    onClick={() => handleManageWorkflow('add-photos')}
                  >
                    Add photos
                  </button>
                  <div className="rounded-xl border border-white/[0.06] bg-black/20 p-3 sm:p-5 space-y-3 min-w-0 overflow-x-hidden">
                    <p className="text-sm font-medium text-white">Vehicle identity</p>
                    <EditVehicleModal
                      variant="panel"
                      isOpen={manageBuildOpen}
                      onClose={() => undefined}
                      vehicle={
                        {
                          id: vehicle.id,
                          year: vehicle.year,
                          make: vehicle.make,
                          model: vehicle.model,
                          trim: vehicle.trim,
                          color: vehicle.color,
                          nickname: vehicle.nickname,
                          description: vehicle.description,
                          chassis: vehicle.chassis,
                          engine: vehicle.engine,
                          transmission: vehicle.transmission,
                          drivetrain: vehicle.drivetrain,
                          build_status: vehicle.build_status,
                          exterior_finish_type: vehicle.exterior_finish_type,
                          exterior_finish_color: vehicle.exterior_finish_color,
                          exterior_finish_brand: vehicle.exterior_finish_brand,
                          exterior_finish_style: vehicle.exterior_finish_style,
                          slug: vehicle.slug,
                          public_slug: vehicle.public_slug || vehicle.slug,
                          owner_username: owner.username,
                          is_public: vehicle.is_public !== false,
                          is_primary: Boolean(vehicle.is_primary),
                          horsepower: performance?.horsepower ?? undefined,
                          horsepower_measurement: performance?.horsepower_measurement ?? undefined,
                          torque: performance?.torque ?? undefined,
                          redline: performance?.redline ?? undefined,
                          performance: performance
                            ? {
                                horsepower: performance.horsepower ?? undefined,
                                horsepower_measurement:
                                  performance.horsepower_measurement ?? undefined,
                                torque: performance.torque ?? undefined,
                                redline: performance.redline ?? undefined,
                              }
                            : undefined,
                          settings: {
                            show_pricing: displaySettings.showPricing !== false,
                            show_performance: displaySettings.showPerformance !== false,
                          },
                        } as Vehicle
                      }
                      onVehicleUpdated={handleVehicleUpdated}
                    />
                  </div>
                  <div className="rounded-xl border border-white/[0.06] bg-black/20 p-3 sm:p-5 space-y-3 min-w-0 overflow-x-hidden">
                    <p className="text-sm font-medium text-white">Visibility</p>
                    <VehicleSharingModal
                      variant="panel"
                      isOpen={manageBuildOpen}
                      onClose={() => undefined}
                      vehicleId={vehicle.id}
                      vehicleName={vehicle.nickname || vehicleTitle}
                      onPublicChange={(isPublic) =>
                        setVehicleData((prev) =>
                          prev
                            ? { ...prev, vehicle: { ...prev.vehicle, is_public: isPublic } }
                            : prev
                        )
                      }
                      onSharingChange={handleSharingChange}
                      onPendingPrivacySave={setPrivacySavePending}
                    />
                  </div>
                  <div className="rounded-xl border border-white/[0.06] bg-black/20 p-3 sm:p-5 space-y-3 min-w-0 overflow-x-hidden">
                    <p className="text-sm font-medium text-white">Build story and look</p>
                    <BuildProfileEditorModal
                      variant="panel"
                      isOpen={manageBuildOpen}
                      onClose={() => undefined}
                      vehicleId={vehicle.id}
                      onSaved={(profile) => {
                        applyBuildProfile(profile);
                        void refreshExpansion();
                      }}
                    />
                  </div>
                </div>
              ),
              performance: (
                <PerformanceRecordManager
                  vehicleId={vehicle.id}
                  embedded
                  onChanged={() => {
                    void refreshExpansion();
                  }}
                />
              ),
              maintenance: <MaintenancePanel vehicleId={vehicle.id} />,
              contributors: (
                <ContributorsManagePanel
                  vehicleId={vehicle.id}
                  isOpen={manageBuildOpen}
                  onChanged={() => {
                    void refreshExpansion();
                  }}
                />
              ),
              certification: (
                <VehicleCertificationPanel
                  vehicleId={vehicle.id}
                  isPublic={vehicle.is_public !== false}
                  embedded
                />
              ),
              advanced: (
                <SettingsModal
                  variant="panel"
                  isOpen={manageBuildOpen}
                  onClose={() => undefined}
                  settings={displaySettings}
                  onSave={handleSettingsSave}
                  onImportClick={() => handleManageWorkflow('import')}
                  onDeleteVehicle={handleDeleteVehicle}
                  onDuplicated={isOwner ? handleDuplicated : undefined}
                  ownerUsername={owner.username}
                  vehicle={
                    {
                      id: vehicle.id,
                      year: vehicle.year,
                      make: vehicle.make,
                      model: vehicle.model,
                      trim: vehicle.trim,
                      nickname: vehicle.nickname,
                      public_slug: vehicle.public_slug || vehicle.slug,
                      slug: vehicle.slug,
                      display_name: vehicleTitle,
                    } as Vehicle
                  }
                  vehicleName={vehicle.nickname || vehicleTitle}
                  modifications={editableMods}
                  isOwner={canEdit}
                />
              ),
            }}
          />
          {isAddModificationModalOpen && (
            <AddModificationModal
              isOpen={isAddModificationModalOpen}
              onClose={() => {
                setIsAddModificationModalOpen(false);
                setAddModificationParent(null);
              }}
              onSubmit={handleModificationAdded}
              existingModifications={editableMods as never}
              vendors={allVendors}
              refreshVendors={refreshVendors}
              initialParent={addModificationParent}
            />
          )}
          {isEditVehicleModalOpen && (
            <EditVehicleModal
              isOpen={isEditVehicleModalOpen}
              onClose={() => setIsEditVehicleModalOpen(false)}
              vehicle={
                {
                  id: vehicle.id,
                  year: vehicle.year,
                  make: vehicle.make,
                  model: vehicle.model,
                  trim: vehicle.trim,
                  color: vehicle.color,
                  nickname: vehicle.nickname,
                  description: vehicle.description,
                  chassis: vehicle.chassis,
                  engine: vehicle.engine,
                  transmission: vehicle.transmission,
                  drivetrain: vehicle.drivetrain,
                  build_status: vehicle.build_status,
                  exterior_finish_type: vehicle.exterior_finish_type,
                  exterior_finish_color: vehicle.exterior_finish_color,
                  exterior_finish_brand: vehicle.exterior_finish_brand,
                  exterior_finish_style: vehicle.exterior_finish_style,
                  slug: vehicle.slug,
                  public_slug: vehicle.public_slug || vehicle.slug,
                  owner_username: owner.username,
                  is_public: vehicle.is_public !== false,
                  is_primary: Boolean(vehicle.is_primary),
                  horsepower: performance?.horsepower ?? undefined,
                  horsepower_measurement: performance?.horsepower_measurement ?? undefined,
                  torque: performance?.torque ?? undefined,
                  redline: performance?.redline ?? undefined,
                  performance: performance
                    ? {
                        horsepower: performance.horsepower ?? undefined,
                        horsepower_measurement: performance.horsepower_measurement ?? undefined,
                        torque: performance.torque ?? undefined,
                        redline: performance.redline ?? undefined,
                      }
                    : undefined,
                  settings: {
                    show_pricing: displaySettings.showPricing !== false,
                    show_performance: displaySettings.showPerformance !== false,
                  },
                } as Vehicle
              }
              onVehicleUpdated={handleVehicleUpdated}
            />
          )}
          {isSettingsModalOpen && (
            <SettingsModal
              isOpen={isSettingsModalOpen}
              onClose={() => setIsSettingsModalOpen(false)}
              settings={displaySettings}
              onSave={handleSettingsSave}
              onImportClick={() => setIsImportModificationsModalOpen(true)}
              onDeleteVehicle={handleDeleteVehicle}
              onDuplicated={isOwner ? handleDuplicated : undefined}
              ownerUsername={owner.username}
              vehicle={
                {
                  id: vehicle.id,
                  year: vehicle.year,
                  make: vehicle.make,
                  model: vehicle.model,
                  trim: vehicle.trim,
                  nickname: vehicle.nickname,
                  public_slug: vehicle.public_slug || vehicle.slug,
                  slug: vehicle.slug,
                  display_name: vehicleTitle,
                } as Vehicle
              }
              vehicleName={vehicle.nickname || vehicleTitle}
              modifications={editableMods}
              isOwner={canEdit}
            />
          )}
          {isImageUploadModalOpen && (
            <VehicleImageUploadModal
              isOpen={isImageUploadModalOpen}
              onClose={() => setIsImageUploadModalOpen(false)}
              vehicleId={vehicle.id}
              heroImageCount={activeGallery.length}
              error={imageUploadError}
              onErrorClear={() => setImageUploadError(null)}
              onImageUploaded={handleImageUploaded}
              onError={setImageUploadError}
            />
          )}
          {isEditModificationModalOpen && editingModification && (
            <EditModificationModal
              isOpen={isEditModificationModalOpen}
              onClose={() => setIsEditModificationModalOpen(false)}
              modification={editingModification}
              onSubmit={handleModificationUpdated}
              existingModifications={editableMods as never}
              vendors={allVendors}
              refreshVendors={refreshVendors}
            />
          )}
          {isImportModificationsModalOpen && (
            <ImportModificationsModal
              vehicleId={vehicle.id}
              isOpen={isImportModificationsModalOpen}
              onClose={() => setIsImportModificationsModalOpen(false)}
              onImportComplete={async () => {
                await reloadEditableMods(vehicle.id);
                setIsImportModificationsModalOpen(false);
                showToast('Your build list has been added', 'success');
                void refreshVehicle();
              }}
              vehicleInfo={{ make: vehicle.make, model: vehicle.model, year: vehicle.year }}
            />
          )}
          {isEnhanceModalOpen && (
            <EnhanceModificationsModal
              isOpen={isEnhanceModalOpen}
              vehicleId={vehicle.id}
              onClose={() => setIsEnhanceModalOpen(false)}
              onApplied={async () => {
                await reloadEditableMods(vehicle.id);
                void refreshVehicle();
              }}
            />
          )}
          {isAiEditModalOpen && (
            <AiEditModificationsModal
              isOpen={isAiEditModalOpen}
              vehicleId={vehicle.id}
              onClose={() => setIsAiEditModalOpen(false)}
              onApplied={async () => {
                await reloadEditableMods(vehicle.id);
                showToast('Build updates applied', 'success');
                void refreshVehicle();
              }}
            />
          )}
          {aiEditPremiumGateOpen ? (
            <div
              className="fixed inset-0 z-[120] flex items-end justify-center bg-black/70 p-4 sm:items-center"
              role="dialog"
              aria-modal="true"
              aria-label="Edit with AI Premium feature"
              onClick={() => setAiEditPremiumGateOpen(false)}
            >
              <div
                className="w-full max-w-md rounded-xl border border-orange-500/25 bg-[#111] p-5 shadow-xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="mb-2 flex items-center gap-2">
                  <h3 className="text-base font-semibold text-white">Edit with AI</h3>
                  <PremiumBadge featureLabel="Edit with AI" />
                </div>
                <p className="text-sm leading-relaxed text-zinc-400">
                  Make changes to your build by describing them in plain language. Bilt:Spec
                  prepares the changes for you to review before anything is applied.
                </p>
                <p className="mt-3 text-sm text-zinc-500">Included with Bilt:Spec Premium.</p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <Link to="/settings/billing" onClick={() => setAiEditPremiumGateOpen(false)}>
                    <Button type="button" size="sm">
                      Try Premium
                    </Button>
                  </Link>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setAiEditPremiumGateOpen(false)}
                  >
                    Not now
                  </Button>
                </div>
              </div>
            </div>
          ) : null}
          <ImageTitleEditModal
            isOpen={imageTitleEditIndex !== null}
            initialTitle={
              imageTitleEditIndex !== null ? activeGallery[imageTitleEditIndex]?.title || '' : ''
            }
            onClose={() => setImageTitleEditIndex(null)}
            onSave={handleSaveImageTitle}
          />
          {deleteImageConfirm !== null && (
            <div className="modal-overlay z-[9999]">
              <div className="modal-panel-sm">
                <h3 className="heading-card mb-2">Delete Image</h3>
                <p className="text-muted mb-4 text-sm">
                  Are you sure you want to delete this image? This action cannot be undone.
                </p>
                <div className="flex gap-3 justify-end">
                  <button
                    type="button"
                    onClick={() => setDeleteImageConfirm(null)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      void confirmDeleteImage();
                    }}
                    className="btn-danger"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Owner Share modal: Link · QR & sticker · Share card · Analytics */}
      {canEdit && (
        <VehicleSharingModal
          isOpen={shareModalOpen}
          onClose={() => {
            setShareModalOpen(false);
            setShareModalTab('settings');
          }}
          vehicleId={vehicle.id}
          vehicleName={vehicle.nickname || vehicleTitle}
          initialTab={shareModalTab}
          onPublicChange={(isPublic) =>
            setVehicleData((prev) =>
              prev ? { ...prev, vehicle: { ...prev.vehicle, is_public: isPublic } } : prev
            )
          }
          onSharingChange={handleSharingChange}
          onPendingPrivacySave={setPrivacySavePending}
          ownerTools={
            shareCardsEnabled
              ? {
                  enabled: true,
                  shareUrl,
                  qrImageUrl,
                  onEnsureQrImage: ensureQrImage,
                  vehicle: {
                    year: vehicle.year,
                    make: vehicle.make,
                    model: vehicle.model,
                    trim: vehicle.trim,
                    nickname: vehicle.nickname,
                  },
                  ownerName: owner.display_name || owner.username,
                  imageUrl: activeGallery[0]?.src || vehicleData.meta.image,
                  stats: [],
                  achievements,
                  records: expansion?.performance_records ?? [],
                  awards: awards || [],
                  investedAmount: totalModCost,
                }
              : null
          }
        />
      )}

      {canEdit && !showcaseMode ? (
        <>
          <Modal
            isOpen={perfManagerOpen}
            onClose={() => setPerfManagerOpen(false)}
            title="Performance records"
            variant="sheet"
          >
            <PerformanceRecordManager
              vehicleId={vehicle.id}
              onChanged={() => {
                void refreshExpansion();
              }}
            />
          </Modal>
          <BuildProfileEditorModal
            isOpen={profileEditorOpen}
            onClose={() => setProfileEditorOpen(false)}
            vehicleId={vehicle.id}
            onSaved={(profile) => {
              applyBuildProfile(profile);
              void refreshExpansion();
            }}
          />
          <ContributorRequestModal
            isOpen={contributorRequestOpen}
            onClose={() => setContributorRequestOpen(false)}
            vehicleId={vehicle.id}
            onCreated={() => {
              void refreshExpansion();
            }}
          />
        </>
      ) : null}

      {!showcaseMode && <Footer />}
    </div>
  );

  if (!isOwner && !showcaseMode) {
    return (
      <BuildViewTracker
        vehicleId={vehicle.id}
        source={referrer === 'qr' ? 'qr' : referrer || null}
        onViewCount={(nextCount) => setLiveViewCount(nextCount)}
      >
        {pageBody}
      </BuildViewTracker>
    );
  }

  return pageBody;
};

export default VehiclePage;
